import dayjs from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import {
  EndOfSupportRow,
  EndOfSupportStatus,
} from '@totem/components/deviceEndOfSupport/types';
import { DeviceQueryResult } from '@totem/types/devices';

export const NOT_RECORDED = 'Not recorded';

export const STATUS: Record<
  EndOfSupportStatus,
  { label: string; color: string; rank: number }
> = {
  past: { label: 'Past end of service', color: 'red', rank: 0 },
  soon: { label: 'Ending within 12 months', color: 'gold', rank: 1 },
  ok: { label: 'In service', color: 'green', rank: 2 },
  check: { label: 'Date needs checking', color: 'orange', rank: 3 },
  none: { label: 'No date on file', color: 'default', rank: 4 },
};

// An End of Support date earlier than the OS release can't belong to that OS; it's almost certainly
// the hardware's date. Extend this list as other OS conflicts turn up.
const OS_RELEASE_DATES: [string, string][] = [
  ['Windows 11', '2021-10-05'],
  ['Windows 10', '2015-07-29'],
  ['Windows Server 2022', '2021-08-18'],
  ['Windows Server 2019', '2018-10-02'],
];

// Lifecycle dates are YYYY-MM-DD strings; "" and "Not yet announced" mean no date.
const asDate = (value?: string) =>
  value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;

const referenceId = (id?: string) => (id && id !== EMPTY_ID ? id : null);

export const daysUntil = (date: string) =>
  dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');

export const formatDate = (date: string) => dayjs(date).format('MMM D, YYYY');

const statusOf = (eos: string | null, os: string): EndOfSupportStatus => {
  if (!eos) {
    return 'none';
  }
  if (
    OS_RELEASE_DATES.some(
      ([name, released]) => os.includes(name) && eos < released,
    )
  ) {
    return 'check';
  }
  const days = daysUntil(eos);
  if (days < 0) {
    return 'past';
  }
  return days <= 365 ? 'soon' : 'ok';
};

export const toRow = (result: DeviceQueryResult): EndOfSupportRow => {
  const { device } = result;
  const record = device.endOfLife?.[0];
  const eos = asDate(record?.endOfSupportDate);
  const eol = asDate(record?.endOfLifeDate);
  const os = device.operatingSystem ?? '';

  return {
    id: device.id,
    name: device.displayName,
    type: device.type ?? '',
    vendor: device.vendor ?? '',
    os,
    firmware: device.firmware ?? '',
    managed: device.flags?.managed?.status === 'ACTIVE',
    productType: record?.productType ?? '',
    discontinued: asDate(record?.discontinuedDate),
    eos,
    eol,
    estimated: Boolean(record?.source),
    buildingId: referenceId(result.building?.id),
    building: (result.building?.name ?? '').trim(),
    region: (result.region?.name ?? '').trim(),
    controlSystemId: referenceId(result.controlSystem?.id),
    controlSystem: (result.controlSystem?.name ?? '').trim(),
    status: statusOf(eos, os),
    eolFirst: Boolean(eos && eol && eol < eos),
  };
};

// "Hardware date" / "OS date", plus "estimated" when the date came from an automated source.
export const dateBasis = (row: EndOfSupportRow) => {
  if (!row.eos) {
    return '';
  }
  const basis =
    row.productType === 'Operating System' ? 'OS date' : 'Hardware date';
  return row.estimated ? `${basis} · estimated` : basis;
};

// Worst first, then soonest End of Support.
export const defaultOrder = (left: EndOfSupportRow, right: EndOfSupportRow) =>
  STATUS[left.status].rank - STATUS[right.status].rank ||
  (left.eos ?? '9999').localeCompare(right.eos ?? '9999') ||
  left.name.localeCompare(right.name);

export type FilterKey =
  | 'status'
  | 'managed'
  | 'type'
  | 'vendor'
  | 'os'
  | 'productType'
  | 'building'
  | 'controlSystem';

// The value each filterable column filters on.
export const FILTER_VALUE: Record<FilterKey, (row: EndOfSupportRow) => string> =
  {
    status: (row) => STATUS[row.status].label,
    managed: (row) => (row.managed ? 'Managed' : 'Not managed'),
    type: (row) => row.type || NOT_RECORDED,
    vendor: (row) => row.vendor || NOT_RECORDED,
    os: (row) => row.os || NOT_RECORDED,
    productType: (row) => row.productType || NOT_RECORDED,
    building: (row) => row.building || NOT_RECORDED,
    controlSystem: (row) => row.controlSystem || NOT_RECORDED,
  };

export const FILTER_LABEL: Record<FilterKey, string> = {
  status: 'Status',
  managed: 'Managed',
  type: 'Type',
  vendor: 'Vendor',
  os: 'Operating system',
  productType: 'Date applies to',
  building: 'Building',
  controlSystem: 'Control system',
};

export const CSV_HEADERS = [
  'Device',
  'Status',
  'Managed',
  'Type',
  'Vendor',
  'Operating system',
  'Firmware',
  'Date applies to',
  'Discontinued',
  'End of support',
  'End of life',
  'Building',
  'Region',
  'Control system',
  'Date basis',
];

export const toCsvRow = (row: EndOfSupportRow) => [
  row.name,
  STATUS[row.status].label,
  row.managed ? 'Managed' : 'Not managed',
  row.type,
  row.vendor,
  row.os,
  row.firmware,
  row.productType,
  row.discontinued ?? '',
  row.eos ?? '',
  row.eol ?? '',
  row.building,
  row.region,
  row.controlSystem,
  dateBasis(row),
];
